import React, { useMemo, useState } from "react";
import ElapsedClock from "./ElapsedClock";
const rectsOverlap = (a, b) =>
  a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

const tableSessionLabel = (session) => {
  const status = String(session?.status || "").toUpperCase();
  if (!session) return "";
  if (["PENDING", "REQUESTED", "PENDIENTE"].includes(status)) return `Solicitud ${session.source || "QR"} pendiente`;
  if (session.has_active_order) return "Pedido e-commerce activo";
  return "Cliente e-commerce autorizado";
};

export default function RfmGridView({
  zone,
  tables = [],
  objects = [],
  statuses = [],

  selectedTableId = null,
  selectedObjectId = null,

  editMode = false,
  zoomLevel = 1,
  cellSize = 55, // IMPORTANTE: debe coincidir con tu diseño

  onCellClick, // (x,y) => void
  onCreateTableAt,
  onSelectTable, // (id) => void
  onSelectObject, // (id) => void
  onMoveTable, // (id, x, y) => void
  onMoveObject, // (id, x, y) => void
  updateTable, // (id, data) => void
  updateObject, // (id, data) => void
}) {
  const cols = zone?.cols ?? 12;
  const rows = zone?.rows ?? 12;

  const zoneTables = useMemo(
    () => tables.filter((t) => t.zone === zone?.id),
    [tables, zone?.id]
  );
  const zoneObjects = useMemo(
    () => objects.filter((o) => o.zone === zone?.id),
    [objects, zone?.id]
  );

  // Drag state
  const [dragging, setDragging] = useState(null); // { kind:'table'|'object', id, w, h }
  const [hoverRect, setHoverRect] = useState(null); // {x,y,w,h}

  // Occupied cells map (para bloquear celdas multi-cell)
  const occupied = useMemo(() => {
    const set = new Set();

    // tables: marcamos solo las celdas "secundarias" como occupied-cell (igual que tu HTML)
    zoneTables.forEach((t) => {
      const w = t.gridW || 1;
      const h = t.gridH || 1;
      for (let dy = 0; dy < h; dy++) {
        for (let dx = 0; dx < w; dx++) {
          if (dx === 0 && dy === 0) continue;
          set.add(`${t.gridX + dx},${t.gridY + dy}`);
        }
      }
    });

    // objects: marcamos TODAS sus celdas
    zoneObjects.forEach((o) => {
      const w = o.gridW || 1;
      const h = o.gridH || 1;
      for (let dy = 0; dy < h; dy++) {
        for (let dx = 0; dx < w; dx++) {
          set.add(`${o.gridX + dx},${o.gridY + dy}`);
        }
      }
    });

    return set;
  }, [zoneTables, zoneObjects]);

  // Helpers de px
  const pxW = (wCells) => wCells * cellSize + (wCells - 1);
  const pxH = (hCells) => hCells * cellSize + (hCells - 1);
  const pxLeft = (x) => x * (cellSize + 1) + 1;
  const pxTop = (y) => y * (cellSize + 1) + 1;

  const statusById = useMemo(() => {
    const m = new Map();
    statuses.forEach((s) => m.set(s.id, s));
    return m;
  }, [statuses]);

  const canPlace = (kind, id, x, y, w, h) => {
    // bounds
    if (x < 0 || y < 0) return false;
    if (x + w > cols || y + h > rows) return false;

    const targetRect = { x, y, w, h };

    // colisión con otros objetos
    for (const o of zoneObjects) {
      if (kind === "object" && o.id === id) continue;
      const r = { x: o.gridX, y: o.gridY, w: o.gridW || 1, h: o.gridH || 1 };
      if (rectsOverlap(targetRect, r)) return false;
    }

    // colisión con otras mesas
    for (const t of zoneTables) {
      if (kind === "table" && t.id === id) continue;
      const r = { x: t.gridX, y: t.gridY, w: t.gridW || 1, h: t.gridH || 1 };
      if (rectsOverlap(targetRect, r)) return false;
    }

    return true;
  };

  const onDragStartItem = (kind, item) => (e) => {
    if (!editMode) {
      e.preventDefault();
      return;
    }
    const w = item.gridW || 1;
    const h = item.gridH || 1;
    setDragging({ kind, id: item.id, w, h });
    setHoverRect({ x: item.gridX, y: item.gridY, w, h });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", `${kind}-${item.id}`);
  };

  const onDragEndItem = () => {
    setDragging(null);
    setHoverRect(null);
  };

  const onCellDragOver = (x, y) => (e) => {
    if (!editMode || !dragging) return;
    e.preventDefault();
    setHoverRect({ x, y, w: dragging.w, h: dragging.h });
  };

  const onCellDrop = (x, y) => async (e) => {
    if (!editMode || !dragging) return;
    e.preventDefault();

    const { kind, id, w, h } = dragging;
    const ok = canPlace(kind, id, x, y, w, h);
    if (!ok) {
      setDragging(null);
      setHoverRect(null);
      return;
    }

    if (kind === "table") {
      //We move the table
      onMoveTable?.(id, x, y);
      //We update the table in the backend
      let data = {
        posx: x,
        posy: y
      }

      updateTable(id, data);
    }

    if (kind === "object") {
      onMoveObject?.(id, x, y);
      //We update the object in the backend
      let data = {
        posx: x,
        posy: y
      }
      updateObject(id, data);
    }
    setDragging(null);
    setHoverRect(null);
  };

  const isCellInHover = (x, y) => {
    if (!hoverRect) return false;
    return (
      x >= hoverRect.x &&
      x < hoverRect.x + hoverRect.w &&
      y >= hoverRect.y &&
      y < hoverRect.y + hoverRect.h
    );
  };

  // Render grid cells
  const cells = useMemo(() => {
    const out = [];
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const isOcc = occupied.has(`${x},${y}`);
        const dragOver = editMode && dragging && isCellInHover(x, y);

        out.push(
          <div
            key={`${x}-${y}`}
            className={[
              "rfm-grid-cell",
              isOcc ? "occupied-cell" : "",
              dragOver ? "drag-over" : "",
            ].join(" ")}
            data-x={x}
            data-y={y}
            style={{ width: cellSize, height: cellSize }}
            // onClick={() => onCellClick?.(x, y)}

            onClick={() => {
              //We permit placing a new table only if we're in edit mode and there's no dragging (to avoid conflicts)
              if (!editMode) return;

              // ✅ new table
              onCreateTableAt?.(x, y);
            }}


            onDragOver={onCellDragOver(x, y)}
            onDrop={onCellDrop(x, y)}
          />
        );
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cols, rows, occupied, cellSize, editMode, dragging, hoverRect, onCellClick]);

  return (
    <div
      className="rfm-grid-wrapper"
      style={{
        transform: `scale(${zoomLevel})`,
        transformOrigin: "top left",
      }}
    >
      <div style={{ position: "relative" }}>
        <div
          className="rfm-grid"
          style={{
            gridTemplateColumns: `repeat(${cols}, ${cellSize}px)`,
          }}
        >
          {cells}
        </div>

        {/* Objects */}
        {zoneObjects.map((o) => {
          const w = pxW(o.gridW || 1);
          const h = pxH(o.gridH || 1);
          const left = pxLeft(o.gridX);
          const top = pxTop(o.gridY);

          const icon = o.type === "plant" ? "🌿" : "";
          const label = o.type === "plant" ? icon : o.label || "";

          return (
            <div
              key={`obj-${o.id}`}
              className={[
                "rfm-object",
                o.type,
                selectedObjectId === o.id ? "selected" : "",
                editMode && dragging?.kind === "object" && dragging.id === o.id
                  ? "dragging"
                  : "",
              ].join(" ")}
              style={{ width: w, height: h, left, top }}
              draggable={editMode}
              onClick={(e) => {
                e.stopPropagation();
                onSelectObject?.(o.id);
              }}
              onDragStart={onDragStartItem("object", o)}
              onDragEnd={onDragEndItem}
            >
              {label}
            </div>
          );
        })}

        {/* Tables */}
        {zoneTables.map((t) => {
          const w = pxW(t.gridW || 1);
          const h = pxH(t.gridH || 1);
          const left = pxLeft(t.gridX);
          const top = pxTop(t.gridY);

          const shape = t.shape || "square";
          const isMerged = Array.isArray(t.mergedWith) && t.mergedWith.length > 0;

          // badges
          const mergedBadge = isMerged ? (
            <div className="rfm-merged-badge">
              <i className="bi bi-link-45deg" />
              Merged
            </div>
          ) : null;

          const timeBadge =
            Number.isFinite(t.elapsed_since) && (t.status === "occupied" || t.status === "reserved") ? (
              <div className="rfm-table-time">
                <i className="bi bi-clock" /> <ElapsedClock since={t.elapsed_since} />
              </div>
            ) : null;

          const totalBadge =
            t.status === "occupied" && (t.total || 0) > 0 ? (
              (() => {
                const remaining = (t.total || 0) - (t.paid || 0);
                if (remaining <= 0) return null;
                return (
                  <div className="rfm-table-total">
                    ${Math.round(remaining)}
                  </div>
                );
              })()
            ) : null;

          // (opcional) si statusById tiene color/icon, puedes usarlo luego
          const _st = statusById.get(t.status);

          return (
            <div
              key={`tbl-${t.id}`}
              className={[
                "rfm-table",
                t.status,
                `shape-${shape}`,
                selectedTableId === t.id ? "selected" : "",
                isMerged ? "merged" : "",
                editMode && dragging?.kind === "table" && dragging.id === t.id
                  ? "dragging"
                  : "",
              ].join(" ")}
              style={{ width: w, height: h, left, top }}
              draggable={editMode}
              onClick={(e) => {
                e.stopPropagation();
                onSelectTable?.(t.id);
              }}
              onDragStart={onDragStartItem("table", t)}
              onDragEnd={onDragEndItem}
            >
              {mergedBadge}
              {timeBadge}
              {t.hasPendingAlert ? (
                <span className="rfm-table-alert-dot" title="Actividad nueva sin revisar" />
              ) : null}
              <span className="rfm-table-number">{t.number}</span>
              <span className="rfm-table-info">{t.chairs}p</span>
              {t.tableSession ? (
                <span className={`rfm-table-session-badge ${String(t.tableSession.status || "").toLowerCase()}`}>
                  {tableSessionLabel(t.tableSession)}
                </span>
              ) : null}
              {t.tableSession && Number(t.tableSession.participant_count) > 1 ? (
                <span className="rfm-table-participants-badge" title="Participantes en la sesion e-commerce">
                  <i className="bi bi-people-fill" /> {t.tableSession.participant_count} participantes en ecommerce
                </span>
              ) : null}
              {totalBadge}

              {/* <div style={{
                position: "absolute",
                left: 10,
                top: 10,
                zIndex: 9999,
                background: "red",
                color: "white",
                padding: "6px 10px",
                borderRadius: 8
              }}>
                zoneTables: {zoneTables.length}
              </div> */}
            </div>
          );
        })}
      </div>
    </div>
  );
}
